import { ApolloError, useMutation, useReactiveVar } from '@apollo/client'
import {
    ShowGlobalSnackbarMutation,
    ShowGlobalSnackbarMutationProps,
    ShowGlobalSnackbarRequest,
} from '@deltek/specification-client/core/Configurations/GlobalSnackbar'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { FormControl, TextField } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { makeStyles, Theme } from '@material-ui/core/styles'
import { AddAssociationModifier } from 'api/graphQL/mutations/project.mutations'
import { ownerId } from 'api/graphQL/reactiveVars/projectHome'
import clsx from 'clsx'
import { AssociationModifierInput } from 'models/projectInput'
import React, { ChangeEvent } from 'react'

const useStyles = makeStyles((theme: Theme) => ({
    dialogTitle: {
        '& .MuiTypography-h6': {
            color: '#304959',
            fontSize: pxToRem(34),
        },
        '&.MuiDialogTitle-root': {
            paddingTop: theme.spacing(1.875),
            paddingRight: theme.spacing(0),
            paddingLeft: theme.spacing(3.125),
        },
    },
    dialogCont: {
        color: theme.palette.common.black,
        width: 630,
        height: 87,
        fontSize: pxToRem(18),
    },
    actionButton: {
        color: '#A78966',
        fontWeight: 400,
        fontSize: pxToRem(18),
        lineHeight: pxToRem(40),
        letterSpacing: pxToRem(0.25),
        '&.MuiButton-text:last-child': {
            marginRight: theme.spacing(3),
        },
    },
    confirmationText: {
        paddingTop: theme.spacing(2),
        fontSize: pxToRem(18),
        color: theme.palette.common.black,
    },
    dialogAction: {
        '&.MuiDialogActions-root': {
            paddingLeft: theme.spacing(0),
            paddingRight: theme.spacing(0),
        },
    },
    projectOwnerTextField: {
        '& label': {
            '& span': {
                '&.MuiInputLabel-asterisk': {
                    color: 'red',
                },
            },
        },
    },
    ProjectOwnerDialogContainer: {
        '&.ProjectOwnerDialogPosition': {
            zIndex: '1305 !important',
        },
    },
}))

export interface ProjectOwnerDialogProps {
    /**
     * Defines state of confirmation dialog.
     */
    openDialog: boolean

    /**
     * For closing confirmation dialog
     */
    handleDialogClose: VoidFunction

    /**
     * Refetch data on save
     */
    refetch: VoidFunction
}
const ProjectOwnerDialog: React.FC<ProjectOwnerDialogProps> = ({
    openDialog,
    handleDialogClose,
    refetch,
}) => {
    const classes = useStyles()

    const [errorMessage, setErrorMessage] = React.useState<String>('')
    const [hasError, setHasError] = React.useState<boolean>(false)
    const [projectOwner, setProjectOwner] = React.useState<string>('')

    const [textfieldref, setTextFieldRef] = React.useState<
        React.RefObject<HTMLElement> | null | HTMLElement
    >(null)

    const [isFormDirty, setIsFormDirty] = React.useState<boolean>(false)

    const [showGlobalSnackbar] = useMutation<
        ShowGlobalSnackbarRequest,
        ShowGlobalSnackbarMutationProps
    >(ShowGlobalSnackbarMutation)

    useReactiveVar(ownerId)

    const handleProjectOwnerChange = (
        e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
    ) => {
        setHasError(false)
        setIsFormDirty(true)
        setErrorMessage('')
        setProjectOwner(e.currentTarget.value)
    }

    const handleProjectOwnerSave = () => {
        if (projectOwner.length > 0) {
            const obj: AssociationModifierInput = {
                modifierListId: ownerId(),
                name: projectOwner,
            }

            createOwner({
                variables: {
                    associationModifierInput: obj,
                },
            })
        } else {
            setHasError(true)
            setErrorMessage('Owner Name required')
        }
    }

    const [createOwner] = useMutation<{
        projectGroupInput: AssociationModifierInput
    }>(AddAssociationModifier, {
        onCompleted: () => {
            showGlobalSnackbar({
                variables: {
                    message: `Owner created successfully.`,
                    variant: 'success',
                    id: `ProjectHomePage_OwnerCreateSuccess`,
                },
            })

            handleDialogClose()
            refetch()
        },
        onError: (error: ApolloError) => {
            setErrorMessage(error.message)
            setHasError(true)
        },
    })

    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        handleProjectOwnerSave()
    }

    React.useEffect(() => {
        if (openDialog) {
            setErrorMessage('')
            setIsFormDirty(false)
            setHasError(false)
        }
    }, [openDialog])

    React.useEffect(() => {
        if (textfieldref) {
            setTimeout(() => {
                ;(textfieldref as HTMLElement).focus()
            }, 0)
        }
    }, [textfieldref])

    return (
        <Dialog
            className={clsx(
                classes.ProjectOwnerDialogContainer,
                'ProjectOwnerDialogPosition'
            )}
            open={openDialog}
            data-testid="projectOwnerAddDialog"
            aria-labelledby="add-project-owner-properties"
        >
            <DialogTitle className={classes.dialogTitle}>
                {'Create an Owner'}
            </DialogTitle>
            <DialogContent className={classes.dialogCont}>
                <form onSubmit={(e) => handleSubmit(e)}>
                    <FormControl fullWidth variant="outlined">
                        <TextField
                            inputRef={setTextFieldRef}
                            data-testid="projectOwnerField"
                            label={'Project Owner Name'}
                            disabled={false}
                            name="projectRole"
                            value={projectOwner}
                            placeholder={'Enter Name Here'}
                            required={true}
                            error={hasError}
                            helperText={hasError && errorMessage}
                            onChange={(event) => {
                                handleProjectOwnerChange(event)
                            }}
                            inputProps={{
                                maxLength: 100,
                                'aria-autocomplete': 'none',
                                autoComplete: 'chrome-off',
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            className={classes.projectOwnerTextField}
                        />
                    </FormControl>
                </form>
            </DialogContent>
            <DialogActions className={classes.dialogAction}>
                <Button
                    data-testid="cancelAddButton"
                    className={classes.actionButton}
                    onClick={handleDialogClose}
                >
                    Cancel
                </Button>
                <Button
                    disabled={projectOwner.length === 0 || !isFormDirty}
                    data-testid="saveAddButton"
                    onClick={handleProjectOwnerSave}
                    className={classes.actionButton}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ProjectOwnerDialog
